import React, { FunctionComponent, useEffect, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { DateTime } from 'luxon'
import { get, pick, isEmpty } from 'lodash'
import accounting from 'accounting'

import {
  DateTimeDisplay,
  GenericButton,
  PageDescription,
  PageHeading,
  Tabs,
} from '../../../../components'
import Table from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'
import { HistoryTableContext } from './index'

import { State } from '../../../../reducers'
import {
  getFilteredPayments,
  exportPayments,
} from '../../../../actions/payments'
import { pruneObject } from '../../../../utils/objectManagement'

import { PaymentsData, PaymentHistory } from '../../../../typings/payments'
import { ReportStatus } from '../../../../typings/report'

import styles from '../styles.module.css'

const History: FunctionComponent = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const {
    sort,
    pagination: { page, size },
    changePage,
    changePageSize,
    changeSort,
  } = useContext(HistoryTableContext)

  const paymentHistoryLoading = useSelector<State, boolean>(
    state => get(state, 'payments.paymentHistory.status') === 'fetching'
  )

  const paymentHistory = useSelector<State, PaymentsData>(
    state => state.payments.paymentHistory
  )

  const paymentHistoryTotals = useSelector<State, PaymentsData['totals']>(
    state => get(state, 'payments.paymentHistory.totals')
  )

  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.payments.paymentHistory.export, [
      'hasData',
      'status',
      'error',
    ]),
    loading: state.payments.paymentHistory.export.status === 'fetching',
    hasError: !isEmpty(state.payments.paymentHistory.export.error),
  }))

  useEffect(() => {
    dispatch(
      getFilteredPayments('paymentHistory', {
        page,
        size,
        ...pruneObject(sort),
      })
    )
  }, [sort, page, size])

  return (
    <>
      <PageHeading title="Payments">
        <Tabs
          items={[
            {
              title: 'Outstanding Balance',
              isSelected: false,
              to: './outstanding-balance',
            },
            {
              title: 'Payment History',
              isSelected: true,
              to: './history',
            },
          ]}
        />

        <ReportActions
          className={styles.actions}
          fetchExport={() =>
            dispatch(exportPayments('paymentHistory', null, pruneObject(sort)))
          }
          exportStatus={exportStatus}
        />
      </PageHeading>

      <PageDescription content="This report reflects all historical payments processed in your account within the last 18 months." />

      <Table
        className={styles.table}
        isLoading={paymentHistoryLoading}
        onSortChange={changeSort}
        sort={sort}
        data={paymentHistory.transactions as PaymentHistory[]}
        pagination={{
          pageSize: size,
          currentPage: page,
          total: get(paymentHistory, 'pagination.total', 1),
          onPaginate: changePage,
          onPageSizeUpdate: changePageSize,
        }}
        cols={[
          {
            text: 'Payment ID',
            field: 'paymentId',
          },
          {
            text: 'Date Sent',
            field: 'dateSent',
            sortable: true,
            render: row =>
              row.dateSent && (
                <DateTimeDisplay datetime={DateTime.fromSQL(row.dateSent)} />
              ),
          },
          {
            text: 'Amount',
            field: 'amount',
            sortable: true,
            className: styles.right,
            render: ({ amount }) => accounting.formatMoney(amount, '$'),
          },
          {
            text: 'Status',
            field: 'status',
            sortable: true,
            className: styles.capitalize,
          },
          {
            text: '',
            className: styles.nostretch,
            render: ({ paymentId }) => (
              <GenericButton
                onClick={() => {
                  history.push(`./payment-details/${paymentId}`, {
                    previousUrl: location.pathname + location.search,
                  })
                }}
                small
              >
                View
              </GenericButton>
            ),
          },
        ]}
        totalRow={[
          {
            text: 'Total',
            colSpan: 2,
            className: styles.footerHeader,
          },
          {
            text: accounting.formatMoney(
              get(paymentHistoryTotals, 'amount', 0),
              '$'
            ),
            className: styles.right,
          },
          {
            text: '',
            colSpan: 2,
          },
        ]}
      />
    </>
  )
}

export default History
